import React, { useState, useEffect } from 'react';
import { Shield, Award } from 'lucide-react';
import axios from 'axios';
import { useCMS } from '../hooks/useCMS';
import { useSchemaOrg } from '../hooks/useSchemaOrg';
import { getMediaUrl } from '../utils/mediaUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const TrustBar = ({ variant = 'full', className = '' }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const { get } = useCMS('trustbar');

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/partners?active_only=true`);
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const { name: companyName } = useSchemaOrg();

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": companyName,
    "memberOf": partners.map(partner => ({
      "@type": "Organization",
      "name": partner.name,
      "url": partner.url
    })),
    "hasCredential": partners.map(partner => ({
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "certification",
      "name": partner.name,
      "description": partner.description
    }))
  };

  if (loading || partners.length === 0) {
    return null;
  }

  // Compact variant for footer
  if (variant === 'compact') {
    return (
      <div className={`py-6 ${className}`}>
        <div className="flex items-center justify-center mb-4">
          <Shield className="w-4 h-4 text-gray-500 mr-2" />
          <span className="text-xs text-gray-500 uppercase tracking-wider">{get('trustbar_compact_title', 'Sertifisert og godkjent')}</span>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-6">
          {partners.map((partner) => (
            <a
              key={partner.id}
              href={partner.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="group transition-opacity hover:opacity-80"
              title={partner.description}
            >
              {partner.logo && typeof partner.logo === 'string' && partner.logo.length > 0 ? (
                <img
                  src={getMediaUrl(partner.logo)}
                  alt={`${partner.name} - Offisiell partner av Sand-tec AS`}
                  className="h-6 w-auto grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all"
                  loading="lazy"
                />
              ) : (
                <span className="text-xs text-gray-500 font-medium">{partner.name}</span>
              )}
            </a>
          ))}
        </div>
      </div>
    );
  }

  // Full variant for homepage
  return (
    <section 
      className={`relative py-12 bg-gradient-to-b from-gray-900 to-black ${className}`}
      aria-label={get('trustbar_aria_label', 'Sertifiseringer og partnerskap')}
    >
      {/* SEO: Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(239,68,68,0.05),transparent_70%)]"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 rounded-full border border-gray-700">
            <Award className="w-5 h-5 text-yellow-500" />
            <span className="text-sm text-gray-300 font-medium">{get('trustbar_title', 'Sertifisert og godkjent av')}</span>
          </div>
        </div>

        {/* Logo Grid - First-View Content for AI crawlers */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6 items-center">
          {partners.map((partner) => (
            <article
              key={partner.id}
              className="group flex flex-col items-center text-center"
            >
              <a
                href={partner.url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 bg-gray-800/30 rounded-xl border border-gray-700/50 hover:border-red-600/30 transition-all w-full"
                title={partner.description}
              >
                {partner.logo && typeof partner.logo === 'string' && partner.logo.length > 0 ? (
                  <img
                    src={getMediaUrl(partner.logo)}
                    alt={`${partner.name} - Offisiell partner av Sand-tec AS`}
                    className="h-12 w-auto mx-auto grayscale group-hover:grayscale-0 transition-all duration-300"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-12 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-gray-600 group-hover:text-red-500 transition-colors" />
                  </div>
                )}
              </a>
              
              {/* Partner name - visible in DOM for crawlers */}
              <h3 className="mt-3 text-sm font-medium text-gray-400 group-hover:text-white transition-colors">
                {partner.name}
              </h3>
              
              {/* Description - hidden visually but in DOM for SEO */}
              <p className="sr-only">{partner.description}</p>
            </article>
          ))}
        </div>

        {/* Trust indicators */}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-10 pt-8 border-t border-gray-800">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Shield className="w-4 h-4 text-green-500" />
            <span>{get('trustbar_indicator_1', 'Godkjent kontrollorgan')}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Award className="w-4 h-4 text-yellow-500" />
            <span>{get('trustbar_indicator_2', 'Sertifiserte teknikere')}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Shield className="w-4 h-4 text-blue-500" />
            <span>{get('trustbar_indicator_3', 'Miljøsertifisert')}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustBar;
